// /src/components/tools/memberProfileCard/PerkThemeHeatCanvas.jsx
// WebGL heat haze — object-fit:cover refraction of theme media (image or HLS).

import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import Hls from 'hls.js';
import { isHlsMedia } from '../../../utils/memberProfileThemeDisplay';

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG = `
#ifdef GL_ES
precision mediump float;
#endif

varying vec2 vUv;
uniform float uTime;
uniform float uIntensity;
uniform vec2 uTexSize;
uniform vec2 uViewSize;
uniform sampler2D uImage;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 345.45));
  p += dot(p, p + 34.345);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}

// Match CSS object-fit: cover (view UV → texture UV).
vec2 coverUv(vec2 uv) {
  float texAspect = uTexSize.x / max(uTexSize.y, 1.0);
  float viewAspect = uViewSize.x / max(uViewSize.y, 1.0);
  vec2 mapped = uv;
  if (texAspect > viewAspect) {
    float s = viewAspect / texAspect;
    mapped.x = uv.x * s + (1.0 - s) * 0.5;
  } else {
    float s = texAspect / viewAspect;
    mapped.y = uv.y * s + (1.0 - s) * 0.5;
  }
  return clamp(mapped, 0.001, 0.999);
}

void main() {
  float intensity = clamp(uIntensity, 0.0, 1.0);
  float t = uTime * 0.02;

  // Rising heat columns — readable shimmer, stronger toward bottom
  float rise = t * 0.55;
  vec2 p = vUv * vec2(3.4, 1.6) + vec2(0.0, -rise);
  float n1 = fbm(p);
  float n2 = fbm(p * 2.2 + vec2(2.1, -rise * 1.4));
  float n3 = noise(p * 5.5 + vec2(-1.3, -rise * 2.2));

  vec2 warp;
  warp.x = (n1 - 0.5) * 1.35 + (n2 - 0.5) * 0.7 + (n3 - 0.5) * 0.35;
  warp.y = (n2 - 0.5) * 0.55 + (n1 - 0.5) * 0.25;

  float heightBias = mix(0.55, 1.0, smoothstep(1.0, 0.12, vUv.y));
  float amp = intensity * heightBias;
  // Up to ~5% UV travel — visible heat haze without cartoon wobble
  warp *= amp * 0.052;

  vec2 viewUv = clamp(vUv + warp, 0.0, 1.0);
  vec2 texUv = coverUv(viewUv);

  // Mild chromatic split along warp direction
  vec2 dir = normalize(warp + vec2(0.0002, 0.0001));
  float ca = intensity * 0.0035;
  vec3 col;
  col.r = texture2D(uImage, coverUv(viewUv + dir * ca)).r;
  col.g = texture2D(uImage, texUv).g;
  col.b = texture2D(uImage, coverUv(viewUv - dir * ca)).b;

  gl_FragColor = vec4(col, 1.0);
}
`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const msg = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(msg || 'Shader compile failed');
  }
  return sh;
}

function createProgram(gl, vsSrc, fsSrc) {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc);
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(prog) || 'Program link failed');
  }
  return prog;
}

function loadImageElement(url, useCrossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (useCrossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('texture load failed'));
    img.src = url;
  });
}

async function loadImageForTexture(url) {
  const src = String(url || '').trim();
  if (!src) throw new Error('missing media url');

  if (src.startsWith('blob:') || src.startsWith('data:')) {
    const img = await loadImageElement(src, false);
    return { img };
  }

  let parsed;
  try {
    parsed = new URL(src, window.location.href);
  } catch (_) {
    throw new Error('invalid media url');
  }

  if (parsed.origin === window.location.origin) {
    const img = await loadImageElement(src, false);
    return { img };
  }

  const proxyPaths = [
    '/api/media/fetch-for-canvas',
    '/api/admin/fetch-media-for-canvas',
  ];
  let lastErr = null;
  for (let i = 0; i < proxyPaths.length; i += 1) {
    try {
      const { data } = await axios.get(proxyPaths[i], {
        params: { url: src },
        responseType: 'blob',
        withCredentials: true,
      });
      const blobUrl = URL.createObjectURL(data);
      try {
        const img = await loadImageElement(blobUrl, false);
        return {
          img,
          revoke: () => URL.revokeObjectURL(blobUrl),
        };
      } catch (err) {
        URL.revokeObjectURL(blobUrl);
        throw err;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  try {
    const img = await loadImageElement(src, true);
    return { img };
  } catch (_) {
    throw lastErr || new Error('texture load failed');
  }
}

function createFallbackHeatCanvas() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 512;
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  const g = ctx.createLinearGradient(0, 512, 0, 0);
  g.addColorStop(0, '#3a1a0c');
  g.addColorStop(0.5, '#243040');
  g.addColorStop(1, '#141c28');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 512, 512);
  return c;
}

function createEmptyTexture(gl) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    1,
    1,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    new Uint8Array([20, 20, 24, 255]),
  );
  return tex;
}

function uploadTextureSource(gl, tex, source) {
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
}

/**
 * @param {{
 *   mediaUrl?: string,
 *   mediaType?: string,
 *   intensity?: number,
 * }} props
 * intensity 0–100 (0 = no visual effect)
 */
export default function PerkThemeHeatCanvas({
  mediaUrl,
  mediaType,
  intensity = 0,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const intensityRef = useRef(intensity);
  const src = String(mediaUrl || '').trim();
  const useVideo = Boolean(src) && isHlsMedia(src, mediaType);
  const intensityClamped = Math.max(0, Math.min(100, Math.round(Number(intensity) || 0)));
  const heatActive = intensityClamped >= 1;

  useEffect(() => {
    intensityRef.current = intensityClamped;
  }, [intensityClamped]);

  // HLS / video source for live texture upload
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !useVideo || !heatActive || !src) return undefined;

    let hlsInstance = null;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.play().catch(() => {});
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    if (!Hls.isSupported()) return undefined;

    hlsInstance = new Hls({
      startLevel: 0,
      autoStartLoad: true,
      capLevelToPlayerSize: true,
      maxBufferLength: 4,
    });
    hlsInstance.on(Hls.Events.MEDIA_ATTACHED, () => hlsInstance.loadSource(src));
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch(() => {});
    });
    hlsInstance.attachMedia(video);

    return () => {
      hlsInstance.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [src, useVideo, heatActive]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas || !heatActive) return undefined;

    const reduceMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) return undefined;

    let cancelled = false;
    let rafId = null;
    let resizeObserver = null;
    let texture = null;
    let program = null;
    let buffer = null;
    let revokeBlob = null;
    let ready = false;
    let uTime = 0;
    let viewW = 0;
    let viewH = 0;
    let texW = 1;
    let texH = 1;
    const locs = {};

    canvas.classList.remove('is-ready');

    const applySize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      if (w === viewW && h === viewH && canvas.width > 0) return;
      viewW = w;
      viewH = h;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const frame = () => {
      if (cancelled || !ready || !program || !texture) return;
      if (viewW < 2 || viewH < 2) {
        applySize();
        rafId = window.requestAnimationFrame(frame);
        return;
      }

      uTime += 1;

      if (useVideo) {
        const video = videoRef.current;
        if (video && video.readyState >= 2 && video.videoWidth > 0) {
          texW = video.videoWidth;
          texH = video.videoHeight;
          try {
            uploadTextureSource(gl, texture, video);
          } catch (_) {
            // ignore transient upload errors
          }
        }
      }

      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(locs.aPos);
      gl.vertexAttribPointer(locs.aPos, 2, gl.FLOAT, false, 0, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(locs.uImage, 0);
      gl.uniform1f(locs.uTime, uTime);
      gl.uniform1f(locs.uIntensity, intensityRef.current / 100);
      gl.uniform2f(locs.uTexSize, texW, texH);
      gl.uniform2f(locs.uViewSize, viewW, viewH);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      canvas.classList.add('is-ready');
      rafId = window.requestAnimationFrame(frame);
    };

    (async () => {
      try {
        program = createProgram(gl, VERT, FRAG);
        buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1,
          ]),
          gl.STATIC_DRAW,
        );

        locs.aPos = gl.getAttribLocation(program, 'aPos');
        locs.uImage = gl.getUniformLocation(program, 'uImage');
        locs.uTime = gl.getUniformLocation(program, 'uTime');
        locs.uIntensity = gl.getUniformLocation(program, 'uIntensity');
        locs.uTexSize = gl.getUniformLocation(program, 'uTexSize');
        locs.uViewSize = gl.getUniformLocation(program, 'uViewSize');

        texture = createEmptyTexture(gl);

        if (useVideo) {
          ready = true;
        } else if (src) {
          const loaded = await loadImageForTexture(src);
          if (cancelled) {
            if (loaded.revoke) loaded.revoke();
            return;
          }
          revokeBlob = loaded.revoke || null;
          texW = loaded.img.naturalWidth || loaded.img.width || 1;
          texH = loaded.img.naturalHeight || loaded.img.height || 1;
          uploadTextureSource(gl, texture, loaded.img);
          if (revokeBlob) {
            revokeBlob();
            revokeBlob = null;
          }
          ready = true;
        } else {
          const fallback = createFallbackHeatCanvas();
          texW = fallback.width;
          texH = fallback.height;
          uploadTextureSource(gl, texture, fallback);
          ready = true;
        }

        applySize();
        if (typeof ResizeObserver === 'function') {
          resizeObserver = new ResizeObserver(() => {
            applySize();
          });
          resizeObserver.observe(wrap);
        }
        rafId = window.requestAnimationFrame(frame);
      } catch (err) {
        console.warn('[PerkThemeHeatCanvas]', err?.message || err);
        canvas.classList.remove('is-ready');
      }
    })();

    return () => {
      cancelled = true;
      ready = false;
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      if (revokeBlob) revokeBlob();
      if (texture) gl.deleteTexture(texture);
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
    };
  }, [src, useVideo, heatActive]);

  if (!heatActive) return null;

  return (
    <div
      ref={wrapRef}
      className="perk-theme-overlay__layer perk-theme-overlay__layer--heat-webgl"
      aria-hidden
    >
      {useVideo ? (
        <video
          ref={videoRef}
          className="perk-theme-overlay__heat-video-src"
          muted
          loop
          playsInline
          autoPlay
        />
      ) : null}
      <canvas ref={canvasRef} className="perk-theme-overlay__heat-canvas" />
    </div>
  );
}
